import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/use-toast'
import {
  Loader2, Users, Building2, UserCheck, UserX,
  LayoutGrid, List, ChevronLeft, ChevronRight,
  XCircle, PauseCircle, PlayCircle, Download, MoreHorizontal,
} from 'lucide-react'

// ─── helpers ─────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('')
}

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-emerald-100 text-emerald-800',
  suspended: 'bg-amber-100 text-amber-800',
  inactive:  'bg-zinc-100 text-zinc-600',
}

// ─── component ───────────────────────────────────────────────────────────────

interface Props {
  platformRole?: string
}

export function AllUsersView({ platformRole }: Props) {
  const { toast } = useToast()
  const qc = useQueryClient()

  // view state
  const [viewMode,     setViewMode]     = useState<'table' | 'cards'>('table')
  const [search,       setSearch]       = useState('')
  const [filterOrg,    setFilterOrg]    = useState('all')
  const [filterRole,   setFilterRole]   = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [page,         setPage]         = useState(1)
  const [pageSize,     setPageSize]     = useState(20)
  const [selectedIds,  setSelectedIds]  = useState<Set<string>>(new Set())
  const [bulking,      setBulking]      = useState(false)

  // ── data ──────────────────────────────────────────────────────────────────

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['admin-all-institution-members'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('organization_members')
        .select(`
          id, user_id, status, joined_at,
          organizations(name, settings),
          roles(name)
        `)
        .order('joined_at', { ascending: false })

      if (error) throw error

      const institutionsOnly = ((rows || []) as any[]).filter(
        (m: any) => m.organizations?.settings?.is_platform_owner !== true
      )

      const userIds = [...new Set(
        institutionsOnly.map((m: any) => m.user_id).filter(Boolean)
      )]

      let profileMap: Record<string, { full_name: string; email: string; avatar_url?: string }> = {}
      if (userIds.length > 0) {
        const { data: profiles } = await (supabase as any)
          .from('profiles')
          .select('user_id, full_name, first_name, last_name, email, avatar_url')
          .in('user_id', userIds as string[])
        profileMap = Object.fromEntries(
          (profiles || []).map((p: any) => {
            const composed = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
            const display = composed || p.full_name || p.email || ''
            return [p.user_id, { full_name: display, email: p.email || '', avatar_url: p.avatar_url || '' }]
          })
        )
      }

      return institutionsOnly.map((m: any) => ({
        ...m,
        full_name:  profileMap[m.user_id]?.full_name || m.user_id?.slice(0, 8) || '',
        email:      profileMap[m.user_id]?.email ?? '',
        avatar_url: profileMap[m.user_id]?.avatar_url ?? '',
      })) as any[]
    },
  })

  // ── derived lists ─────────────────────────────────────────────────────────

  const orgs = useMemo(
    () => [...new Set(members.map((m: any) => m.organizations?.name).filter(Boolean))] as string[],
    [members]
  )
  const roles = useMemo(
    () => [...new Set(members.map((m: any) => m.roles?.name).filter(Boolean))] as string[],
    [members]
  )

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return members.filter((m: any) => {
      const matchSearch =
        !q ||
        (m.full_name ?? '').toLowerCase().includes(q) ||
        (m.email ?? '').toLowerCase().includes(q) ||
        (m.organizations?.name ?? '').toLowerCase().includes(q)
      const matchOrg    = filterOrg    === 'all' || m.organizations?.name === filterOrg
      const matchRole   = filterRole   === 'all' || m.roles?.name         === filterRole
      const matchStatus = filterStatus === 'all' || m.status              === filterStatus
      return matchSearch && matchOrg && matchRole && matchStatus
    })
  }, [members, search, filterOrg, filterRole, filterStatus])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage   = Math.min(page, totalPages)
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)

  // stat counts (always from unfiltered members)
  const totalCount     = members.length
  const activeCount    = members.filter((m: any) => m.status === 'active').length
  const suspendedCount = members.filter((m: any) => m.status === 'suspended').length
  const orgCount       = orgs.length

  // ── selection ─────────────────────────────────────────────────────────────

  const pageIds = paginated.map((m: any) => m.id as string)
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id))

  function toggleSelectAll() {
    if (allPageSelected) {
      const next = new Set(selectedIds)
      pageIds.forEach(id => next.delete(id))
      setSelectedIds(next)
    } else {
      const next = new Set(selectedIds)
      pageIds.forEach(id => next.add(id))
      setSelectedIds(next)
    }
  }

  function toggleSelect(id: string) {
    const next = new Set(selectedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelectedIds(next)
  }

  // ── bulk actions ──────────────────────────────────────────────────────────

  async function bulkSetStatus(status: string) {
    setBulking(true)
    try {
      const ids = [...selectedIds]
      const { error } = await supabase
        .from('organization_members')
        .update({ status } as any)
        .in('id', ids)
      if (error) throw error
      toast({ title: `${ids.length} user(s) set to ${status}.` })
      setSelectedIds(new Set())
      qc.invalidateQueries({ queryKey: ['admin-all-institution-members'] })
    } catch (e: any) {
      toast({ title: 'Action failed.', description: e.message, variant: 'destructive' })
    } finally {
      setBulking(false)
    }
  }

  async function setStatusOne(memberId: string, status: string) {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ status } as any)
        .eq('id', memberId)
      if (error) throw error
      toast({ title: `User ${status}.` })
      qc.invalidateQueries({ queryKey: ['admin-all-institution-members'] })
    } catch (e: any) {
      toast({ title: 'Action failed.', description: e.message, variant: 'destructive' })
    }
  }

  function exportCSV() {
    const header = ['Name', 'Email', 'Institution', 'Role', 'Status', 'Joined']
    const rows = filtered.map((m: any) => [
      m.full_name,
      m.email,
      m.organizations?.name ?? '',
      m.roles?.name ?? '',
      m.status,
      new Date(m.joined_at).toLocaleDateString('en-GB'),
    ])
    const csv = [header, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = Object.assign(document.createElement('a'), { href: url, download: 'users.csv' })
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function resetFilters() {
    setSearch(''); setFilterOrg('all'); setFilterRole('all'); setFilterStatus('all'); setPage(1)
  }

  const hasFilters = search || filterOrg !== 'all' || filterRole !== 'all' || filterStatus !== 'all'

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[#0D2240]">All Users</h2>
          <p className="text-justify text-xs text-muted-foreground mt-0.5">
            All institution members across the platform.
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode('table')}
            title="Table view"
          >
            <List className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'cards' ? 'default' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={() => setViewMode('cards')}
            title="Cards view"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Users',  value: totalCount,     icon: Users,     color: 'text-[#0D2240]' },
          { label: 'Active',       value: activeCount,    icon: UserCheck, color: 'text-emerald-600' },
          { label: 'Suspended',    value: suspendedCount, icon: UserX,     color: 'text-amber-600' },
          { label: 'Institutions', value: orgCount,       icon: Building2, color: 'text-violet-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#F7F5F0] border border-[#DDD8CE] rounded-lg p-3 flex items-center gap-3">
            <Icon className={`w-5 h-5 shrink-0 ${color}`} />
            <div>
              <div className="text-xl font-bold text-[#0D2240]">{value}</div>
              <div className="text-[11px] text-muted-foreground">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Search name, email or institution…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          className="max-w-xs h-8 text-sm"
        />

        <Select value={filterOrg} onValueChange={v => { setFilterOrg(v); setPage(1) }}>
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue placeholder="All Institutions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Institutions</SelectItem>
            {orgs.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterRole} onValueChange={v => { setFilterRole(v); setPage(1) }}>
          <SelectTrigger className="w-36 h-8 text-sm">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* status pills */}
        <div className="flex gap-1">
          {['all', 'active', 'suspended'].map(s => (
            <button
              key={s}
              onClick={() => { setFilterStatus(s); setPage(1) }}
              className={[
                'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                filterStatus === s
                  ? 'bg-[#0D2240] text-white border-[#0D2240]'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400',
              ].join(' ')}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-muted-foreground" onClick={resetFilters}>
            <XCircle className="w-3.5 h-3.5" />
            Clear
          </Button>
        )}

        <div className="ml-auto text-xs text-muted-foreground">
          {filtered.length} user{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* bulk toolbar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#0D2240]/5 border border-[#0D2240]/20 rounded-lg text-sm">
          <span className="font-medium text-[#0D2240]">{selectedIds.size} selected</span>
          <div className="flex gap-1 ml-2">
            <Button
              size="sm" variant="outline" className="h-7 text-xs gap-1"
              onClick={() => bulkSetStatus('suspended')} disabled={bulking}
            >
              <PauseCircle className="w-3.5 h-3.5" /> Suspend
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs gap-1"
              onClick={() => bulkSetStatus('active')} disabled={bulking}
            >
              <PlayCircle className="w-3.5 h-3.5" /> Activate
            </Button>
            <Button
              size="sm" variant="outline" className="h-7 text-xs gap-1"
              onClick={exportCSV}
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
          <Button
            size="sm" variant="ghost" className="h-7 text-xs ml-auto text-muted-foreground"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}

      {/* content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
          <Users className="w-8 h-8 opacity-30" />
          No users match the current filters.
        </div>
      ) : viewMode === 'table' ? (

        /* ── TABLE VIEW ── */
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: '#0D2240' }}>
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    checked={allPageSelected}
                    onCheckedChange={toggleSelectAll}
                    className="border-white/40 data-[state=checked]:bg-[#C8A84B] data-[state=checked]:border-[#C8A84B]"
                  />
                </TableHead>
                {['User', 'Institution', 'Role', 'Status', 'Joined', ''].map(h => (
                  <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map((m: any, idx: number) => (
                <TableRow
                  key={m.id}
                  className={[
                    idx % 2 === 1 ? 'bg-[#F7F5F0]' : '',
                    selectedIds.has(m.id) ? '!bg-blue-50' : '',
                  ].join(' ')}
                >
                  <TableCell className="pl-4">
                    <Checkbox
                      checked={selectedIds.has(m.id)}
                      onCheckedChange={() => toggleSelect(m.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold overflow-hidden"
                        style={{ backgroundColor: '#0D2240', color: '#F0E4B8' }}
                      >
                        {m.avatar_url
                          ? <img src={m.avatar_url} className="w-full h-full object-cover" alt="" />
                          : initials(m.full_name)
                        }
                      </div>
                      <div>
                        <div className="text-sm font-medium leading-tight">{m.full_name}</div>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{m.organizations?.name ?? ''}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">{m.roles?.name ?? ''}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[m.status] ?? 'bg-zinc-100 text-zinc-600'}`}>
                      {m.status ? m.status.charAt(0).toUpperCase() + m.status.slice(1) : ''}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(m.joined_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell className="text-right pr-3">
                    {m.status === 'active' ? (
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 gap-1"
                        onClick={() => setStatusOne(m.id, 'suspended')}
                      >
                        <PauseCircle className="w-3 h-3" /> Suspend
                      </Button>
                    ) : (
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1"
                        onClick={() => setStatusOne(m.id, 'active')}
                      >
                        <PlayCircle className="w-3 h-3" /> Activate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      ) : (

        /* ── CARDS VIEW ── */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {paginated.map((m: any) => (
            <div
              key={m.id}
              onClick={() => toggleSelect(m.id)}
              className={[
                'relative border rounded-xl p-4 cursor-pointer transition-colors select-none',
                selectedIds.has(m.id)
                  ? 'border-[#0D2240] bg-blue-50'
                  : 'border-[#DDD8CE] bg-white hover:border-[#0D2240]/40',
              ].join(' ')}
            >
              {/* checkbox corner */}
              <div
                className="absolute top-3 right-3"
                onClick={e => { e.stopPropagation(); toggleSelect(m.id) }}
              >
                <Checkbox checked={selectedIds.has(m.id)} />
              </div>

              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-xs font-bold overflow-hidden"
                  style={{ backgroundColor: '#0D2240', color: '#F0E4B8' }}
                >
                  {m.avatar_url
                    ? <img src={m.avatar_url} className="w-full h-full object-cover" alt="" />
                    : initials(m.full_name)
                  }
                </div>
                <div className="min-w-0 flex-1 pr-6">
                  <div className="font-medium text-sm leading-tight truncate">{m.full_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-xs text-zinc-500 truncate max-w-[120px]">
                      {m.organizations?.name ?? ''}
                    </span>
                    {m.roles?.name && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{m.roles.name}</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[m.status] ?? 'bg-zinc-100 text-zinc-600'}`}>
                      {m.status ? m.status.charAt(0).toUpperCase() + m.status.slice(1) : ''}
                    </span>
                    <button
                      onClick={e => { e.stopPropagation(); setStatusOne(m.id, m.status === 'active' ? 'suspended' : 'active') }}
                      className={`text-[10px] font-medium underline ${m.status === 'active' ? 'text-amber-600' : 'text-emerald-600'}`}
                    >
                      {m.status === 'active' ? 'Suspend' : 'Activate'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── pagination ── */}
      {!isLoading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-4 pt-1 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Show</span>
            {[10, 20, 50].map(n => (
              <button
                key={n}
                onClick={() => { setPageSize(n); setPage(1) }}
                className={[
                  'px-2 py-0.5 rounded border text-xs transition-colors',
                  pageSize === n
                    ? 'bg-[#0D2240] text-white border-[#0D2240]'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400',
                ].join(' ')}
              >
                {n}
              </button>
            ))}
            <span>
              &middot; Showing {Math.min((safePage - 1) * pageSize + 1, filtered.length)}–
              {Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="icon" className="h-7 w-7"
              disabled={safePage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs text-muted-foreground px-2">
              {safePage} / {totalPages}
            </span>
            <Button
              variant="outline" size="icon" className="h-7 w-7"
              disabled={safePage >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
